import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import { getFavoriteBartenders, toggleFavoriteBartender } from "@/lib/favorites.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RouteSkeleton } from "@/components/RouteSkeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/favorites")({
  head: () => ({ meta: [{ title: "Favorites — The Hospitality Reserve" }] }),
  component: FavoritesPage,
});

function FavoritesPage() {
  const [favorites, setFavorites] = useState<{ id: string; full_name: string | null; about_me: string | null; is_verified: boolean; has_dbs: boolean }[]>([]);
  const [stats, setStats] = useState<Record<string, { avg_rating: number | null; completed_count: number }>>({});
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetch = useServerFn(getFavoriteBartenders);
  const toggle = useServerFn(toggleFavoriteBartender);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch();
      setFavorites(res.favorites);
      setStats(res.stats);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load favorites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    try {
      await toggle({ data: { bartender_id: id } });
      setFavorites((prev) => prev.filter((f) => f.id !== id));
      toast.success("Removed from favorites.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not remove.");
    } finally {
      setRemovingId(null);
    }
  };

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return favorites;
    return favorites.filter((f) =>
      (f.full_name || "").toLowerCase().includes(q) ||
      (f.about_me || "").toLowerCase().includes(q),
    );
  }, [favorites, debouncedSearch]);

  if (loading) return <RouteSkeleton cards={6} />;

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="venue" />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="tracking-wide-luxury text-accent">Team</p>
            <h1 className="mt-3 font-display text-3xl">Favorite Bartenders</h1>
            <span className="gold-divider mt-4" />
          </div>
          <Link
            to="/venue"
            className="inline-block border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
          >
            ← Back to Register
          </Link>
        </div>

        {favorites.length > 0 && (
          <div className="mt-6 max-w-md">
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search favorites by name or bio…"
              className="rounded-none"
            />
          </div>
        )}

        {favorites.length === 0 && (
          <div className="mt-8 border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No favorite bartenders yet. Star bartenders from your register to add them here.
          </div>
        )}

        {favorites.length > 0 && filtered.length === 0 && (
          <div className="mt-8 border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No favorites match “{debouncedSearch}”.
          </div>
        )}

        {filtered.length > 0 && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((b) => {
              const s = stats[b.id] ?? { avg_rating: null, completed_count: 0 };
              return (
                <article key={b.id} className="border border-border bg-card p-6 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-xl truncate">{b.full_name || "Unnamed"}</h3>
                    <div className="flex gap-1 shrink-0">
                      {b.is_verified && (
                        <span className="inline-block border border-emerald-700/40 text-emerald-700 px-2 py-0.5 text-[0.6rem] tracking-wide-luxury">Verified</span>
                      )}
                      {b.has_dbs && (
                        <span className="inline-block border border-foreground/20 text-foreground/70 px-2 py-0.5 text-[0.6rem] tracking-wide-luxury">DBS</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-muted-foreground space-y-1">
                    {s.avg_rating !== null && (
                      <p className="text-accent">
                        {"★".repeat(Math.round(s.avg_rating))}{"☆".repeat(5 - Math.round(s.avg_rating))}
                        <span className="ml-1.5 text-muted-foreground">{s.avg_rating.toFixed(1)}</span>
                      </p>
                    )}
                    <p>{s.completed_count} completed shift{s.completed_count === 1 ? "" : "s"}</p>
                  </div>

                  {b.about_me && (
                    <p className="mt-4 text-sm text-foreground/80 line-clamp-3">{b.about_me}</p>
                  )}

                  <div className="mt-auto pt-5 flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={removingId === b.id}
                      onClick={() => handleRemove(b.id)}
                      className="text-xs tracking-wide-luxury text-muted-foreground hover:text-red-700"
                    >
                      {removingId === b.id ? "Removing…" : "Remove"}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
